import React, { useState, useRef, useEffect } from "react";
import { getResponse, GREETING, BOT_NAME, QUICK_REPLIES } from "./chatData";
import "./ChatBot.css";

const BotIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <line x1="8" y1="16" x2="8" y2="16" strokeWidth="3" />
    <line x1="12" y1="16" x2="12" y2="16" strokeWidth="3" />
    <line x1="16" y1="16" x2="16" y2="16" strokeWidth="3" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

function formatMessage(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

/* ── Web Audio pop sound ────────────────────────────────────────────
   Two-tone "ding" generated in-browser — no audio file needed. */
function playPop() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const makeNote = (freq, startAt, dur) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, ctx.currentTime + startAt);
      gain.gain.setValueAtTime(0, ctx.currentTime + startAt);
      gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + startAt + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startAt + dur);
      osc.start(ctx.currentTime + startAt);
      osc.stop(ctx.currentTime + startAt + dur);
    };
    makeNote(880, 0,    0.18); // A5 — first ding
    makeNote(660, 0.14, 0.22); // E5 — second ding
  } catch (_) { /* AudioContext unavailable — silent fail */ }
}

export default function ChatBot() {
  const [open,           setOpen]           = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: GREETING, chips: QUICK_REPLIES },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const bottomRef   = useRef(null);
  const inputRef    = useRef(null);
  const autoHideRef = useRef(null);

  /* Show tooltip + play pop after preloader finishes (preloader = 1.6 s) */
  useEffect(() => {
    const showTimer = setTimeout(() => {
      setTooltipVisible(true);
      playPop();
      // Auto-dismiss tooltip after 8 s if user ignores it
      autoHideRef.current = setTimeout(() => setTooltipVisible(false), 8000);
    }, 2600);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(autoHideRef.current);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      inputRef.current?.focus();
    }
  }, [messages, open]);

  const handleSend = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    setMessages((prev) => [...prev, { from: "user", text: trimmed }]);
    setInput("");
    setTyping(true);

    setTimeout(() => {
      const result = getResponse(trimmed);
      setMessages((prev) => [...prev, { from: "bot", text: result.text, chips: result.chips }]);
      setTyping(false);
    }, 600);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  return (
    <div className="chatbot-root">
      <div className={`chatbot-window ${open ? "chatbot-window--open" : ""}`}>
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-info">
            <div className="chatbot-avatar"><BotIcon /></div>
            <div>
              <div className="chatbot-header-name">{BOT_NAME}</div>
              <div className="chatbot-header-status"><span className="chatbot-dot" />Online</div>
            </div>
          </div>
          <button className="chatbot-close" onClick={() => setOpen(false)} aria-label="Close chat">
            <CloseIcon />
          </button>
        </div>

        {/* Messages */}
        <div className="chatbot-messages">
          {messages.map((msg, i) => (
            <div key={i}>
              <div className={`chatbot-msg chatbot-msg--${msg.from}`}>
                {msg.from === "bot" && <div className="chatbot-msg-avatar"><BotIcon /></div>}
                <div className="chatbot-msg-bubble">
                  {msg.text.split("\n").map((line, j, arr) => (
                    <span key={j}>{formatMessage(line)}{j < arr.length - 1 && <br />}</span>
                  ))}
                </div>
              </div>
              {/* Quick reply chips — only show on the last bot message */}
              {msg.from === "bot" && msg.chips?.length > 0 && i === messages.length - 1 && (
                <div className="chatbot-chips">
                  {msg.chips.map((chip, ci) => (
                    <button key={ci} className="chatbot-chip" onClick={() => handleSend(chip)}>
                      {chip}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {typing && (
            <div className="chatbot-msg chatbot-msg--bot">
              <div className="chatbot-msg-avatar"><BotIcon /></div>
              <div className="chatbot-msg-bubble chatbot-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="chatbot-input-row">
          <input
            ref={inputRef}
            type="text"
            className="chatbot-input"
            placeholder="Ask me anything..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            maxLength={300}
          />
          <button className="chatbot-send" onClick={() => handleSend(input)} disabled={!input.trim()} aria-label="Send">
            <SendIcon />
          </button>
        </div>
      </div>

      {/* Tooltip balloon — pops up on page load */}
      {tooltipVisible && !open && (
        <div className="chatbot-tooltip" role="dialog" aria-label="Chat invitation">
          <button
            className="chatbot-tooltip-close"
            onClick={() => { setTooltipVisible(false); clearTimeout(autoHideRef.current); }}
            aria-label="Dismiss"
          >×</button>
          <p className="chatbot-tooltip-hi">👋 Hey there!</p>
          <p className="chatbot-tooltip-msg">
            I'm <strong>Navodya's assistant</strong>. Ask me about skills,
            experience, or how to get in touch!
          </p>
          <div className="chatbot-tooltip-arrow" aria-hidden="true" />
        </div>
      )}

      {/* Toggle button */}
      <button
        className={`chatbot-toggle ${open ? "chatbot-toggle--open" : ""}`}
        onClick={() => {
          setOpen((v) => !v);
          setTooltipVisible(false);
          clearTimeout(autoHideRef.current);
        }}
        aria-label="Toggle chat"
      >
        {open ? <CloseIcon /> : <BotIcon />}
        {!open && !tooltipVisible && <span className="chatbot-toggle-badge" />}
      </button>
    </div>
  );
}
